import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../../lib/api';
import { useMyTenant } from '../../../hooks/useMyTenant';
import { formatDate } from '../../../lib/clientApprovalPortal';
import OtherWorkspacePrompt, { projectWorkspace } from '../OtherWorkspacePrompt';

interface PartnerLink {
  partnerAccountId: string;
  partnerName: string;
  deliveryModel: string;
  youAreClientContact: boolean;
  openUnlinkRequest: { id: string; requestedAt: string } | null;
}

/**
 * #3187 APPROVE.4 — "Request to end partnership" for a MANAGED client contact
 * (design v1.1 §5.1 item 3, R-5). APPROVE.1a's API, unchanged
 * (client-partner-links.router.ts, mounted at /tenants/:id/partner-links):
 *   GET    /                                  links, youAreClientContact, openUnlinkRequest
 *   POST   /:partnerAccountId/unlink-request  { reason? }  the contact asks
 *   DELETE /:partnerAccountId/unlink-request             the requester withdraws
 * The partner admin or a platform admin completes it. Shown only for MANAGED
 * links whose recorded contact is the viewer; the server re-checks both.
 *
 * 2c's rule (#3185 6537648337 item 2; R1 P3-7, ruling 6587483695): the card
 * acts on the SELECTED workspace, so it shows only when that is the project's
 * own workspace (GET /projects/:id, as 2c reads it). Another workspace: the
 * card is hidden and 2c's switch prompt shows. Not yet known: nothing.
 */
export default function EndPartnershipRequest({ projectId }: { projectId: string }) {
  const { myTenant } = useMyTenant();
  const [projectTenantId, setProjectTenantId] = useState<string | null>(null);
  const workspace = projectWorkspace(projectTenantId, myTenant?.tenantId);
  const tenantId = workspace === 'selected' ? (myTenant?.tenantId ?? null) : null;
  const [links, setLinks] = useState<PartnerLink[]>([]);
  const [asking, setAsking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) { setLinks([]); return; }
    try {
      const r = await apiFetch<{ links?: PartnerLink[] }>(`/tenants/${encodeURIComponent(tenantId)}/partner-links`);
      setLinks((r.links ?? []).filter((l) => l.deliveryModel === 'MANAGED' && l.youAreClientContact));
    } catch {
      setLinks([]);
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setProjectTenantId(null);
    apiFetch<{ tenantId?: string }>(`/projects/${encodeURIComponent(projectId)}`)
      .then((p) => { if (!cancelled) setProjectTenantId(p.tenantId ?? null); })
      .catch(() => { if (!cancelled) setProjectTenantId(null); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  if (workspace === 'other' && projectTenantId) {
    return <section data-testid="end-partnership-other-workspace"><OtherWorkspacePrompt projectTenantId={projectTenantId} /></section>;
  }
  if (!tenantId || links.length === 0) return null;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setAsking(null);
      setReason('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const base = (id: string) => `/tenants/${encodeURIComponent(tenantId)}/partner-links/${encodeURIComponent(id)}/unlink-request`;

  return (
    <section data-testid="end-partnership" style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Your delivery partner</h2>
      {error && <div data-testid="end-partnership-error" role="alert" style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {links.map((l) => (
        <div key={l.partnerAccountId} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', marginBottom: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>{l.partnerName} manages this workspace.</div>
          {l.openUnlinkRequest ? (
            <div data-testid={`end-partnership-open-${l.partnerAccountId}`}>
              You asked to end this partnership on {formatDate(l.openUnlinkRequest.requestedAt)}. Your partner or a platform admin will complete it.{' '}
              <button type="button" data-testid={`end-partnership-withdraw-${l.partnerAccountId}`} disabled={busy}
                onClick={() => { void act(() => apiFetch(base(l.partnerAccountId), { method: 'DELETE' })); }}
                style={{ marginLeft: 6, padding: '2px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}>
                Withdraw request
              </button>
            </div>
          ) : asking === l.partnerAccountId ? (
            <div data-testid={`end-partnership-form-${l.partnerAccountId}`}>
              <p style={{ margin: '0 0 6px' }}>
                When your partner completes this, you become this workspace&apos;s administrator, and integrations they connected may need reconnecting.
              </p>
              <textarea aria-label="Reason (optional)" placeholder="Reason (optional)" value={reason} maxLength={2000}
                data-testid={`end-partnership-reason-${l.partnerAccountId}`}
                onChange={(e) => setReason(e.target.value)}
                style={{ width: '100%', minHeight: 60, padding: 8, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, marginBottom: 8 }} />
              <button type="button" data-testid={`end-partnership-send-${l.partnerAccountId}`} disabled={busy}
                onClick={() => { void act(() => apiFetch(base(l.partnerAccountId), { method: 'POST', body: JSON.stringify(reason.trim() ? { reason: reason.trim() } : {}) })); }}
                style={{ padding: '6px 14px', fontSize: 13, borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', marginRight: 8 }}>
                Send request
              </button>
              <button type="button" disabled={busy} onClick={() => { setAsking(null); setReason(''); }}
                style={{ padding: '6px 14px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" data-testid={`end-partnership-ask-${l.partnerAccountId}`} onClick={() => setAsking(l.partnerAccountId)}
              style={{ padding: '6px 14px', fontSize: 13, borderRadius: 6, border: '1px solid #dc2626', background: 'transparent', color: '#dc2626', cursor: 'pointer' }}>
              Request to end partnership
            </button>
          )}
        </div>
      ))}
    </section>
  );
}
